/**
 * NsectionB3Page
 * B.3.術語定義（A6.4-FORM-METH-002 Section 3. Definitions）
 * Card + DataTable CRUD + Modal
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB3Service, { NsectionB3Detail, DefinitionItem } from '../services/nsectionB3Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const NsectionB3Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB3Detail | null>(null);
  const [definitions, setDefinitions] = useState<DefinitionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalTerm, setModalTerm] = useState('');
  const [modalDefinition, setModalDefinition] = useState('');

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB3Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setDefinitions([]);
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB3Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setDefinitions(result.definitions || []);
        } else {
          setData(null);
          setDefinitions([]);
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  // Modal 操作
  const openModal = (index?: number) => {
    if (index !== undefined) {
      setEditingIndex(index);
      setModalTerm(definitions[index].term);
      setModalDefinition(definitions[index].definition);
    } else {
      setEditingIndex(null);
      setModalTerm('');
      setModalDefinition('');
    }
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    if (!modalTerm.trim()) {
      toast.warning(isZh ? '請輸入術語名稱' : 'Please enter term name');
      return;
    }
    const updated = [...definitions];
    const item: DefinitionItem = { term: modalTerm.trim(), definition: modalDefinition };
    if (editingIndex !== null) {
      updated[editingIndex] = item;
    } else {
      updated.push(item);
    }
    // 按字母排序
    updated.sort((a, b) => a.term.localeCompare(b.term, 'en', { sensitivity: 'base' }));
    setDefinitions(updated);
    setShowModal(false);
  };

  const handleDelete = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此術語嗎？' : 'Are you sure you want to delete this term?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setDefinitions(definitions.filter((_, i) => i !== index));
  };

  // 儲存
  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const result = await nsectionB3Service.update(selectedProposalId, { definitions });
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b3', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b3', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b3" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
            {isZh
              ? '除了「Article 6.4 機制術語表」中所包含的定義外，以下定義適用於本方法論：'
              : "In addition to the definitions contained in the 'Article 6.4 mechanism Glossary of Terms', the following definitions apply for the purpose of this methodology:"}
          </p>

          <div style={{
            width: '100%', background: '#fff', borderRadius: '10px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>
                {isZh ? '術語定義清單' : 'Definitions'}
              </div>
              {canUpdate && (
                <button className="btn-primary" onClick={() => openModal()} style={{ fontSize: '13px', padding: '6px 16px' }}>
                  {t('common.create')}
                </button>
              )}
            </div>

            {definitions.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                {isZh ? '尚無術語定義，請點擊「新增」按鈕' : 'No definitions yet. Click "Create" to add one.'}
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead className="table-header-dark-green">
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '25%' }}>{isZh ? '術語名稱' : 'Term'}</th>
                      <th style={{ width: '55%' }}>{isZh ? '術語定義' : 'Definition'}</th>
                      <th style={{ width: '15%' }}>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {definitions.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td style={{ fontWeight: 600 }}>{item.term}</td>
                        <td dangerouslySetInnerHTML={{ __html: item.definition || '-' }} />
                        <td className="actions">
                          {canUpdate && (
                            <>
                              <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: '8px' }}>
                                {t('common.edit')}
                              </button>
                              <button className="btn-delete" onClick={() => handleDelete(idx)}>
                                {t('common.delete')}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 儲存按鈕 */}
            {canUpdate && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '16px' }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                  {loading ? (isZh ? '確認中...' : 'Confirming...') : t('common.confirm')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯術語' : 'Edit Term') : (isZh ? '新增術語' : 'Add Term')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '術語名稱' : 'Term'} *
                </label>
                <input
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  value={modalTerm}
                  onChange={(e) => setModalTerm(e.target.value)}
                  placeholder={isZh ? '請輸入術語名稱' : 'Enter term name'}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '術語定義' : 'Definition'}
                </label>
                <RichTextEditor
                  value={modalDefinition}
                  onChange={setModalDefinition}
                  placeholder={isZh ? '請輸入術語定義...' : 'Enter definition...'}
                  minHeight="150px"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleModalConfirm}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB3Page;
